import React, { useMemo, use } from 'react';
import EventEmitter from 'eventemitter3';
import { useDispatch } from 'react-redux';
import { cacheReady } from './redux/actions';
import ddt from '@wix/da-ddt';

const logger = ddt.createLogger('rwait');

// Setup EE asap and singleton
const ee = new EventEmitter();
const signalCache = {};

export interface Props {
  cacheName: string;
  skipCache?: boolean;
}

/**
 * The is the Resource Waiter, it uses the new use() hook react provides to
 * suspend a component until the resource cache is ready.
 *
 * Note this cannot be included in SSR content, must wait for mount.
 */
const RWait: React.FC<Props> = React.memo(({ cacheName, skipCache }) => {
  const dispatch = useDispatch();

  if (typeof window !== 'undefined' && !window.__REE__?.on) {
    logger.log('Setting up EE', window.__REE__);
    // eslint-disable-next-line react-compiler/react-compiler
    window.__REE__ = ee;
  }

  const stablePromise = useMemo(() => {
    return new Promise(r => {
      if (typeof document === 'undefined') {
        return;
      }

      logger.log('Starting RWaiter');

      // Allow consumer to skip the cache when they want
      if (skipCache) {
        logger.log('Skipped cache', cacheName);
        return r(true);
      }

      // When its in the cache resolve fast
      if (window.__RCACHE__?.[cacheName]) {
        logger.log('Cache is warm resolving suspense promise', cacheName);
        return r(window.__RCACHE__[cacheName]);
      }

      // Listen for cache ready
      window.__REE__?.on &&
        window.__REE__?.on('cacheReady', name => {
          if (name === cacheName) {
            logger.log(
              'Got cacheReady event, resolving suspense promise',
              cacheName
            );
            r(window.__RCACHE__?.[cacheName]);
          }
        });

      // Flush any queued events for this resource
      // Replay any events sent before EE was available
      // Note we assume one resource per cache
      if (window.__REER__) {
        const replay = window.__REER__;
        if (Array.isArray(replay) && replay.length > 0) {
          replay.forEach((e, i) => {
            if (e && e.d === cacheName) {
              logger.log('Flushing event to EE', cacheName, e);
              window.__REE__?.emit(e.n, e.d);
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              window.__REER__![i] = undefined; // clear queue
            }
          });
        }
      }
    });
  }, [cacheName, skipCache]);

  // On server we cannot suspend because that will cause the server to wait forever
  // so we return early and on client this will either be prefilled when we skip cache
  // or it will trigger the use and show loading + fill redux from the streamed data
  if (typeof document === 'undefined' || skipCache) {
    return null;
  }

  // The new magical use hook...
  const ready = use(stablePromise);

  // We dispatch to redux with the cache data, but only once
  // not ref didn't work here, use() isn't like other hooks so I guess that is why
  if (ready && !signalCache[cacheName] && !skipCache) {
    logger.log('Cache is fetched updating redux', cacheName);
    signalCache[cacheName] = true;
    performance.mark(`RCacheDone:${cacheName}`);
    performance.measure(
      `RWait Redux ${cacheName}': HEAD`,
      'RCacheHead',
      `RCacheDone:${cacheName}`
    );
    performance.measure(
      `RWait Redux ${cacheName}': Ready`,
      'RCacheReady',
      `RCacheDone:${cacheName}`
    );
    performance.measure(
      `RWait Redux ${cacheName}': App`,
      'RCacheAppStartup',
      `RCacheDone:${cacheName}`
    );
    // Schedule for next tick, this seems to fix some inf. loops with this and measured cookie doing
    // something odd with redux.
    setTimeout(
      () => dispatch(cacheReady(cacheName, window.__RCACHE__?.[cacheName])),
      0
    );
  }

  return null;
});
RWait.displayName = 'RWait';

export default RWait;
